import type { ExpectedCommissionRate } from '@commissionsight/sdk';
import { useEffect, useState } from 'react';
import { useAuth } from '../api.js';
import { RateAiDrafter } from '../components/RateAiDrafter.js';
import { RateScheduleEditor } from '../components/RateScheduleEditor.js';
import { Button, Card, Field, inputClass } from '../components/ui.js';
import { useCarriers } from '../lib/useCarriers.js';

const rateTypeLabel = (t: string) =>
  t === 'percent_of_premium' ? '% of premium' : 'Flat per member';

// The API also returns `isEstimate` (a day-one rate auto-seeded from the first
// statement so owed isn't blank) and an optional per-state scope; the SDK type
// doesn't model either yet, so extend locally. Editing the rate clears the estimate.
type RateRow = ExpectedCommissionRate & { isEstimate?: boolean; state?: string | null };

export function Rates() {
  const { client, baseUrl, token } = useAuth();

  // Per-state rates aren't in the SDK's upsert input yet (ACA comp is a per-state
  // PMPM), so post directly — app-only, keeps SDK parity green. Returns rescore count.
  const upsertRate = async (input: {
    carrierId: string;
    planCode: string | null;
    state: string | null;
    rateType: 'percent_of_premium' | 'flat_per_member';
    rateValue: number;
  }): Promise<{ rescoredPeriods: number }> => {
    const res = await fetch(`${baseUrl}/expected-rates`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { title?: string };
      throw new Error(body.title ?? 'Failed to save rate');
    }
    return res.json() as Promise<{ rescoredPeriods: number }>;
  };
  const { carriers } = useCarriers();
  const [rates, setRates] = useState<RateRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Saving a rate re-scores the carrier's processed periods so the dashboard's
  // "commission owed" totals catch up. Surface that it's happening (it's async).
  const noteRescore = (periods: number) =>
    setNote(
      periods > 0
        ? `Recalculating commission owed across ${periods} processed period${periods === 1 ? '' : 's'} — dashboard totals refresh in a moment.`
        : 'Rate saved. It applies to the next statement you process.',
    );

  // Add/edit form state.
  const [carrierId, setCarrierId] = useState('');
  const [planCode, setPlanCode] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [rateType, setRateType] = useState<'percent_of_premium' | 'flat_per_member'>(
    'percent_of_premium',
  );
  const [value, setValue] = useState('');

  // Inline edit: per-row value override + which row is saving.
  const [editVals, setEditVals] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const carrierName = (id: string) => carriers.find((c) => c.id === id)?.name ?? id.slice(0, 8);

  const refresh = () => client.listExpectedRates().then((r) => setRates(r.data as RateRow[]));

  // The number shown in a row's input: percent stored as a fraction shows as %.
  const rowInput = (r: ExpectedCommissionRate) =>
    editVals[r.id] ?? String(r.rateType === 'percent_of_premium' ? r.rateValue * 100 : r.rateValue);

  // Save an edited row — upsert by the same (carrier, plan, state) updates it in place.
  const saveRow = async (r: RateRow) => {
    const num = Number(rowInput(r));
    if (!Number.isFinite(num) || num < 0) {
      setError('Enter a valid, non-negative rate.');
      return;
    }
    const rateValue = r.rateType === 'percent_of_premium' ? num / 100 : num;
    setSavingId(r.id);
    setError(null);
    try {
      const res = await upsertRate({
        carrierId: r.carrierId,
        planCode: r.planCode,
        state: r.state ?? null,
        rateType: r.rateType,
        rateValue,
      });
      noteRescore(res.rescoredPeriods);
      await refresh();
      setEditVals((v) => {
        const next = { ...v };
        delete next[r.id];
        return next;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSavingId(null);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once on mount
  useEffect(() => {
    refresh().catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [client]);

  // Default the carrier picker to the first carrier once the shared list loads.
  useEffect(() => {
    if (!carrierId && carriers[0]) setCarrierId(carriers[0].id);
  }, [carriers, carrierId]);

  const save = async () => {
    if (!carrierId || value === '') return;
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      setError('Enter a valid, non-negative rate.');
      return;
    }
    // Percentages are entered as "20" but stored as the fraction 0.20.
    const rateValue = rateType === 'percent_of_premium' ? num / 100 : num;
    setSaving(true);
    setError(null);
    try {
      const res = await upsertRate({
        carrierId,
        planCode: planCode.trim() || null,
        state: stateCode.trim() || null,
        rateType,
        rateValue,
      });
      noteRescore(res.rescoredPeriods);
      setPlanCode('');
      setStateCode('');
      setValue('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    setError(null);
    await client.deleteExpectedRate(id);
    setNote('Rate removed — recalculating commission owed for that carrier.');
    await refresh();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Expected commission rates</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Tell us what each carrier is contracted to pay you. We compare it to what they actually
          paid to compute <b>commission owed</b> — the recoverable shortfall — on your dashboard.
        </p>
      </div>

      {/* AI rate drafting — paste a comp grid, review, apply. */}
      <RateAiDrafter onApplied={refresh} />

      {/* Product-line schedules (Medicare CMS / ancillary / major-medical) — full
          editor scoped by workspace + carrier. */}
      <RateScheduleEditor carriers={carriers} />

      {error && <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
      {note && !error && (
        <p className="rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{note}</p>
      )}

      <Card>
        <h2 className="mb-4 font-semibold">Add or update a rate</h2>
        <div className="grid gap-4 md:grid-cols-6">
          <Field label="Carrier">
            <select
              className={inputClass}
              value={carrierId}
              onChange={(e) => setCarrierId(e.target.value)}
            >
              {carriers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Plan code (optional)">
            <input
              className={inputClass}
              value={planCode}
              placeholder="all plans"
              onChange={(e) => setPlanCode(e.target.value)}
            />
          </Field>
          <Field label="State (optional)">
            <input
              className={inputClass}
              value={stateCode}
              placeholder="all states"
              maxLength={40}
              onChange={(e) => setStateCode(e.target.value)}
            />
          </Field>
          <Field label="Basis">
            <select
              className={inputClass}
              value={rateType}
              onChange={(e) =>
                setRateType(e.target.value as 'percent_of_premium' | 'flat_per_member')
              }
            >
              <option value="percent_of_premium">% of premium</option>
              <option value="flat_per_member">Flat per member</option>
            </select>
          </Field>
          <Field
            label={rateType === 'percent_of_premium' ? 'Percent (e.g. 20)' : 'Dollars (e.g. 25)'}
          >
            <input
              className={inputClass}
              type="number"
              min="0"
              step="any"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </Field>
          <div className="flex items-end">
            <Button onClick={save} loading={saving}>
              Save rate
            </Button>
          </div>
        </div>
        <p className="mt-2 text-xs text-neutral-500">
          A plan code sets a per-plan override and a state sets a per-state rate (ACA comp is
          usually a per-state PMPM) — leave either blank for the carrier’s default. The most
          specific match wins: plan + state, then plan, then state, then the default. Percentages
          need premium and are evaluated per policy; a flat rate is counted once per member (covered
          life), no matter how many policies they hold.
        </p>
      </Card>

      <Card>
        <h2 className="mb-1 font-semibold">Your rates</h2>
        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {rates.length === 0 && (
            <p className="py-4 text-sm text-neutral-500">
              No rates yet. Add one above to start seeing commission owed.
            </p>
          )}
          {rates.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
              <div className="min-w-0">
                <div className="truncate font-medium">{carrierName(r.carrierId)}</div>
                <div className="text-xs text-neutral-400">
                  {r.planCode ? `Plan ${r.planCode}` : 'All plans'}
                  {r.state ? ` · ${r.state}` : ''} · {rateTypeLabel(r.rateType)}
                  {r.isEstimate && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:bg-amber-950/50 dark:text-amber-400">
                      Estimate — owed is approximate until you confirm your rate
                    </span>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={rowInput(r)}
                    onChange={(e) => setEditVals((v) => ({ ...v, [r.id]: e.target.value }))}
                    className={`w-28 ${inputClass} ${r.rateType === 'percent_of_premium' ? 'pr-7' : 'pl-6'}`}
                  />
                  <span
                    className={`pointer-events-none absolute inset-y-0 flex items-center text-xs text-neutral-400 ${
                      r.rateType === 'percent_of_premium' ? 'right-2.5' : 'left-2.5'
                    }`}
                  >
                    {r.rateType === 'percent_of_premium' ? '%' : '$'}
                  </span>
                </div>
                <Button onClick={() => saveRow(r)} loading={savingId === r.id}>
                  Save
                </Button>
                <Button
                  variant="ghost"
                  className="text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40"
                  onClick={() => remove(r.id)}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
